import { normalizeSlotId } from "@/utils/chargeNotificationSent";
import type { PaymentDetailData } from "../types/consultationsBilling.types";
import { getScheduleServiceCost } from "./getChargeAmount";

export {
  isChargeNotificationSentOnAppointment,
  isChargeNotificationSentOnPayment,
  markAppointmentChargeNotificationSent,
  markPaymentChargeNotificationSent,
  mergeAppointmentsById,
  resolveAppointmentForPaymentContext,
  resolveAppointmentForSlot,
} from "@/utils/chargeNotificationSent";

import {
  isPaymentChargeNotificationSent as isPaymentChargeNotificationSentShared,
  type ChargeNotificationPaymentContext,
} from "@/utils/chargeNotificationSent";

const APPOINTMENT_REFERENCE_URL =
  "http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference";

const ORIGINAL_SERVICE_COST_URL =
  "http://fluxmed.com.br/fhir/StructureDefinition/OriginalServiceCost";

export interface ResolveChargePaymentOptions {
  slots?: fhir5.Slot[];
  displaySchedules?: Array<fhir5.Schedule | { resource?: fhir5.Schedule }>;
}

function normalizeSlotReference(reference?: string | null) {
  const slotId = normalizeSlotId(reference ?? undefined);
  return slotId ? `Slot/${slotId}` : null;
}

function getAppointmentReferenceFromPayment(payment: fhir5.PaymentNotice) {
  return payment.extension?.find((ext) => ext.url === APPOINTMENT_REFERENCE_URL)
    ?.valueReference?.reference;
}

function getAppointmentPatientReference(appointment: fhir5.Appointment) {
  return appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith("Patient/")
  )?.actor?.reference;
}

function patientReferencesMatch(
  appointmentRef?: string,
  paymentRef?: string
): boolean {
  if (!appointmentRef || !paymentRef) return false;
  if (appointmentRef === paymentRef) return true;

  const appointmentTail = appointmentRef.split("/").pop();
  const paymentTail = paymentRef.split("/").pop();
  if (!appointmentTail || !paymentTail) return false;

  return (
    appointmentTail === paymentTail ||
    appointmentTail.endsWith(paymentTail) ||
    paymentTail.endsWith(appointmentTail)
  );
}

function findScheduleBySlot(
  slot: fhir5.Slot,
  displaySchedules: ResolveChargePaymentOptions["displaySchedules"] = []
): fhir5.Schedule | null {
  const scheduleId = slot.schedule?.reference?.split("/")[1];
  if (!scheduleId) return null;

  for (const entry of displaySchedules) {
    const resource =
      entry && typeof entry === "object" && "resource" in entry
        ? entry.resource
        : (entry as fhir5.Schedule);
    if (resource?.id === scheduleId) return resource ?? null;
  }

  return null;
}

function findSlotForAppointment(
  appointment: fhir5.Appointment,
  slots: fhir5.Slot[] = []
) {
  const appointmentSlotId = normalizeSlotId(
    appointment.slot?.[0]?.reference ?? undefined
  );
  if (!appointmentSlotId) return null;

  return (
    slots.find((slot) => slot.id === appointmentSlotId) ??
    slots.find((slot) => normalizeSlotId(`Slot/${slot.id}`) === appointmentSlotId) ??
    null
  );
}

function buildKnownPaymentsBySlotId(knownPayments: PaymentDetailData[]) {
  const bySlotId = new Map<string, PaymentDetailData>();

  knownPayments.forEach((payment) => {
    const slotRef =
      payment.payment.request?.reference ??
      (payment.slot?.id ? `Slot/${payment.slot.id}` : undefined);
    const slotId = normalizeSlotId(slotRef);
    if (slotId && !bySlotId.has(slotId)) {
      bySlotId.set(slotId, payment);
    }
  });

  return bySlotId;
}

function matchesKnownPaymentForAppointment(
  payment: PaymentDetailData,
  appointment: fhir5.Appointment
) {
  if (payment.appointment?.id === appointment.id) return true;

  const appointmentRef = getAppointmentReferenceFromPayment(payment.payment);
  if (appointment.id && appointmentRef === `Appointment/${appointment.id}`) {
    return true;
  }

  const appointmentSlotRef = normalizeSlotReference(
    appointment.slot?.[0]?.reference
  );
  const paymentSlotRef = normalizeSlotReference(
    payment.payment.request?.reference ??
      (payment.slot?.id ? `Slot/${payment.slot.id}` : undefined)
  );

  if (
    appointmentSlotRef &&
    paymentSlotRef &&
    appointmentSlotRef === paymentSlotRef
  ) {
    return true;
  }

  const appointmentPatientRef = getAppointmentPatientReference(appointment);
  const paymentPatientRef = payment.payment.response?.reference;
  return patientReferencesMatch(appointmentPatientRef, paymentPatientRef);
}

function findScheduleForAppointment(
  appointment: fhir5.Appointment,
  displaySchedules: ResolveChargePaymentOptions["displaySchedules"] = []
): fhir5.Schedule | null {
  const locationRef = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith("Location/")
  )?.actor?.reference;
  const practitionerRef = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith("Practitioner/")
  )?.actor?.reference;
  const serviceRef = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith("HealthcareService/")
  )?.actor?.reference;

  if (!locationRef && !practitionerRef) return null;

  for (const entry of displaySchedules) {
    const schedule =
      entry && typeof entry === "object" && "resource" in entry
        ? entry.resource
        : (entry as fhir5.Schedule);
    if (!schedule?.actor?.length) continue;

    const hasLocation =
      !locationRef ||
      schedule.actor.some((actor) => actor.reference === locationRef);
    const hasPractitioner =
      !practitionerRef ||
      schedule.actor.some((actor) => actor.reference === practitionerRef);
    const hasService =
      !serviceRef ||
      schedule.actor.some((actor) => actor.reference === serviceRef);

    if (hasLocation && hasPractitioner && hasService) {
      return schedule ?? null;
    }
  }

  return null;
}

function findPatientForAppointment(
  appointment: fhir5.Appointment,
  patients: fhir5.Patient[]
) {
  return patients.find((patient) =>
    appointment.participant?.some(
      (participant) =>
        participant.actor?.reference === `Patient/${patient.id}` ||
        patientReferencesMatch(
          participant.actor?.reference,
          patient.id ? `Patient/${patient.id}` : undefined
        )
    )
  );
}

function buildSyntheticPaymentNotice(
  slotRef: string,
  patient: fhir5.Patient,
  serviceCost: number
): fhir5.PaymentNotice {
  return {
    status: "active",
    request: { reference: slotRef },
    amount: { value: serviceCost },
    response: patient.id ? { reference: `Patient/${patient.id}` } : undefined,
    ...(serviceCost > 0
      ? {
          extension: [
            {
              url: ORIGINAL_SERVICE_COST_URL,
              valueDecimal: serviceCost,
            },
          ],
        }
      : {}),
  } as fhir5.PaymentNotice;
}

export function resolvePaymentDetailForPendingAppointment(
  appointment: fhir5.Appointment,
  patients: fhir5.Patient[],
  knownPayments: PaymentDetailData[],
  options: ResolveChargePaymentOptions = {}
): PaymentDetailData | null {
  if (appointment.status !== "pending") return null;

  const slotRef = appointment.slot?.[0]?.reference;
  const paymentsBySlotId = buildKnownPaymentsBySlotId(knownPayments);

  const existing =
    knownPayments.find((payment) =>
      matchesKnownPaymentForAppointment(payment, appointment)
    ) ??
    (() => {
      const slotId = normalizeSlotId(slotRef ?? undefined);
      return slotId ? paymentsBySlotId.get(slotId) : undefined;
    })();

  if (existing) return existing;

  const patient = findPatientForAppointment(appointment, patients);
  if (!patient || !slotRef) return null;

  const slot = findSlotForAppointment(appointment, options.slots);
  const schedule =
    (slot ? findScheduleBySlot(slot, options.displaySchedules) : null) ??
    findScheduleForAppointment(appointment, options.displaySchedules);
  const serviceCost = getScheduleServiceCost(schedule);

  return {
    payment: buildSyntheticPaymentNotice(slotRef, patient, serviceCost),
    patient,
    practitioner: null,
    location: null,
    slot,
    schedule,
    appointment,
  };
}

export function resolvePaymentDetailsForAppointments(
  appointments: fhir5.Appointment[],
  patients: fhir5.Patient[],
  knownPayments: PaymentDetailData[],
  options: ResolveChargePaymentOptions = {}
): PaymentDetailData[] {
  return appointments
    .map((appointment) =>
      resolvePaymentDetailForPendingAppointment(
        appointment,
        patients,
        knownPayments,
        options
      )
    )
    .filter((payment): payment is PaymentDetailData => payment !== null);
}

export function canRescheduleAppointment(appointment: fhir5.Appointment) {
  return appointment.status === "booked";
}

export function isPaymentChargeNotificationSent(
  paymentData: PaymentDetailData,
  options?: { appointments?: fhir5.Appointment[] }
): boolean {
  return isPaymentChargeNotificationSentShared(
    paymentData as ChargeNotificationPaymentContext,
    options
  );
}

function pickParticipantDisplay(
  appointment: fhir5.Appointment | null | undefined,
  resourcePrefix: "HealthcareService" | "Practitioner"
) {
  return (
    appointment?.participant?.find((participant) =>
      participant.actor?.reference?.startsWith(`${resourcePrefix}/`)
    )?.actor?.display ?? ""
  );
}

function pickPractitionerName(paymentData: PaymentDetailData) {
  return (
    paymentData.practitioner?.name?.[0]?.text ||
    paymentData.practitioner?.name?.find(
      (entry) => entry.use === "usual" || entry.use === "official"
    )?.given?.join(" ") ||
    ""
  );
}

export function resolveChargeNotificationContext(paymentData: PaymentDetailData) {
  const appointment = paymentData.appointment ?? null;

  const healthcareDisplay =
    paymentData.schedule?.actor?.find((actor) =>
      actor.reference?.includes("HealthcareService")
    )?.display ||
    pickParticipantDisplay(appointment, "HealthcareService") ||
    "";

  const practitionerName =
    paymentData.schedule?.actor?.find((actor) =>
      actor.reference?.includes("Practitioner")
    )?.display ||
    pickParticipantDisplay(appointment, "Practitioner") ||
    pickPractitionerName(paymentData) ||
    "";

  return { healthcareDisplay, practitionerName };
}
